import { useId, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  getVisiblePages,
  formatResultCount,
  getResultRange,
  formatResultRange,
} from "../utils/pagination";
import { useBreakpoint } from "../hooks/useBreakpoint";
import "./Pagination.css";

const GOTO_MIN_TOTAL_PAGES = 5;

export default function Pagination({
  page = 1,
  totalPages = 1,
  totalRecords,
  pageSize,
  currentPageCount,
  onPageChange,
  compact = false,
  className = "",
  ariaLabel = "Pagination",
}) {
  const gotoInputId = useId();
  const isCompact = useBreakpoint(640);
  const isMinimal = useBreakpoint(480);
  const pageMode = isMinimal ? "minimal" : isCompact ? "compact" : "full";
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);
  const showPageNumbers = safeTotalPages > 1 && pageMode !== "minimal";
  const showGoTo = safeTotalPages > GOTO_MIN_TOTAL_PAGES;
  const [gotoPageInput, setGotoPageInput] = useState("");

  const handlePrev = () => {
    if (safePage > 1 && onPageChange) onPageChange(safePage - 1);
  };

  const handleNext = () => {
    if (safePage < safeTotalPages && onPageChange) onPageChange(safePage + 1);
  };

  const handleGoTo = () => {
    const targetP = parseInt(gotoPageInput, 10) || safePage;
    if (targetP >= 1 && targetP <= safeTotalPages) {
      if (targetP !== safePage) {
        onPageChange?.(targetP);
      }
      setGotoPageInput("");
    }
  };

  const visiblePages = showPageNumbers
    ? getVisiblePages(safePage, safeTotalPages, pageMode)
    : [];

  const resultRange = getResultRange(
    safePage,
    pageSize,
    totalRecords,
    currentPageCount,
  );

  const showSummary = resultRange != null;

  return (
    <div className="pagination-bar-root">
    <div
      className={`pagination-bar${compact ? " pagination-bar--compact" : ""}${className ? ` ${className}` : ""}`}
      role="navigation"
      aria-label={ariaLabel}
    >
      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-nav"
          onClick={handlePrev}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
          <span>Previous</span>
        </button>

        {pageMode === "minimal" && safeTotalPages > 1 ? (
          <div className="pagination-current" aria-live="polite">
            Page {safePage} of {safeTotalPages}
          </div>
        ) : null}

        {showPageNumbers && (
          <div className="pagination-pages" aria-label="Page numbers">
            {visiblePages.map((item, idx) =>
              item === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="pagination-ellipsis"
                  aria-hidden="true"
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`pagination-page${item === safePage ? " pagination-page--active" : ""}`}
                  onClick={() => item !== safePage && onPageChange?.(item)}
                  disabled={item === safePage}
                  aria-label={`Page ${item}`}
                  aria-current={item === safePage ? "page" : undefined}
                >
                  {item}
                </button>
              ),
            )}
          </div>
        )}

        <button
          type="button"
          className="pagination-nav"
          onClick={handleNext}
          disabled={safePage >= safeTotalPages}
          aria-label="Next page"
        >
          <span>Next</span>
          <ChevronRight size={16} aria-hidden="true" />
        </button>

        {showGoTo && (
          <div className="pagination-goto">
            <label className="pagination-goto-label" htmlFor={gotoInputId}>
              Go to:
            </label>
            <input
              id={gotoInputId}
              className="pagination-goto-input"
              type="number"
              min={1}
              max={safeTotalPages}
              value={gotoPageInput}
              placeholder={String(safePage)}
              onChange={(e) => setGotoPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleGoTo();
                }
              }}
              aria-label="Go to page"
            />
            <button
              type="button"
              className="pagination-goto-btn"
              onClick={handleGoTo}
            >
              Go
            </button>
          </div>
        )}
      </div>

      {showSummary && (
        <div className="pagination-summary">
          Showing {formatResultRange(resultRange)} of{" "}
          {formatResultCount(totalRecords)} results
        </div>
      )}
    </div>
    </div>
  );
}
